import React, { useMemo, useState, useEffect, useRef } from 'react';
import InfiniteScrollGallery from "../CustomerComponents/TwoDImagesPage/InfiniteScrollGallery";
import environments from '../../../environments';
import axios from 'axios';
import { Tooltip, Modal, Row, Col, Form, Input, Button } from 'antd';
import CustomerMainLayout from "../CustomerComponents/CustomerMainLayout";
import ReactPlayer from 'react-player';
import { addDownloadIconObserver } from '../../../utils/addImageDownload';
import { downloadFile } from '../Utilities';

const Explore = () => {
    const [exploreLoading, setExploreLoading] = useState(false);
    const [exploreImages, setExploreImages] = useState([]);
    const [videoModalVisible, setVideoModalVisible] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const selectedImageRef = useRef(null);
    const [loadingAllData, setLoadingAllData] = useState(false);

    useEffect(() => {
        selectedImageRef.current = selectedImage;
    }, [selectedImage]);

    useEffect(() => {
        const observer = addDownloadIconObserver(() => {
            const img = selectedImageRef.current;
            if (img) {
                const downloadUrl = (img.originalUrl || img.videoUrl || img.src).replace(/_512(?=\.[^.]+$)/, "");
                const extension = downloadUrl.split('.').pop();
                downloadFile(downloadUrl, `Untitled.${extension}`);
            }
        });
        return () => observer.disconnect();
    }, []);

    const mapExploreImages = (rows) => {
        const data = (Array.isArray(rows) ? rows : []).filter(row => row.uri);
        return data.map((row) => {
            const uri = row.uri || "";
            const isVideo = row.image_type === 'ai_video';
            const thumb = (row.thumbnail || uri).replace(/(\.[^.]+)$/, "_512$1");
            const srcPath = isVideo ? uri : uri.replace(/(\.[^.]+)$/, "_compressed.webp");
            const baseURL = row.uri?.startsWith("http") ? "" : environments.getBaseURL();

            return {
                src: row.uri?.startsWith("http") ? (isVideo ? row.uri : row.uri.replace(/(\.[^.]+)$/, "_compressed.webp")) : baseURL + srcPath,
                thumbnail: thumb.startsWith("http") ? thumb : baseURL + thumb,
                videoUrl: isVideo ? (row.uri?.startsWith("http") ? row.uri : baseURL + uri) : undefined,
                originalUrl: row.uri?.startsWith("http") ? row.uri : baseURL + uri,
                display_name: row.display_name,
                product_category: row.product_category || "",
                id: row.id,
                render_id: row.render_id,
                image_type: row.image_type || "ai",
                image_width: row.width,
                image_height: row.height,
            };
        });
    };

    useEffect(() => {
        setExploreLoading(true);

        const fetchAllData = () => {
            setLoadingAllData(true);
            axios.post(environments.AI_ASSETS_HELPER, {
                action: "get_ai_image_public_library"
            }).then((res) => {
                const rows = res.data?.data || res.data || [];
                setExploreImages(mapExploreImages(rows));
                setLoadingAllData(false);
            }).catch(console.error);
        };

        // First fetch 200 items for quick display
        axios.post(environments.AI_ASSETS_HELPER, {
            action: "get_ai_image_public_library",
            limit: 200,
            offset: 0
        }).then((res) => {
            const rows = res.data?.data || res.data || [];
            if (rows.length > 0) {
                setExploreImages(mapExploreImages(rows));
            }
            // After first batch, fetch everything
            fetchAllData();
        }).catch((err) => {
            console.error(err);
            // If first call fails, still try to fetch all
            fetchAllData();
        }).finally(() => {
            setExploreLoading(false);
        });
    }, []);

    const filteredImages = useMemo(() => {
        if (!searchValue.trim()) return exploreImages;
        const searchLower = searchValue.toLowerCase().trim();
        return exploreImages.filter(img => 
            (img.product_category || "").toLowerCase().includes(searchLower) ||
            (img.display_name || "").toLowerCase().includes(searchLower)
        );
    }, [exploreImages, searchValue]);

    const handleVideoClick = React.useCallback((url) => {
        setCurrentVideoUrl(url);
        setVideoModalVisible(true);
    }, []);

    const handleImageClick = React.useCallback((image) => {
        console.log('selected image = ', image)
        setSelectedImage(image);
    }, []);

    return (
        <CustomerMainLayout selected='38' hide_scroll={true} remove_global_padding={true}>
            <div style={{ padding: "20px" }}>
                <Row className="justify-space-between" gutter={[10, 0]} style={{ marginBottom: "20px" }}>
                    <Col lg={10} xl={10}>
                        <h1 className="manrope f-24 w-700" style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 8 }}>
                            Explore 
                            <span style={{ marginLeft: 4, paddingBottom: 2 }} className="manrope f-20">
                                ({searchValue ? filteredImages.length : (exploreImages.length + (loadingAllData ? "+" : ""))})
                            </span>
                        </h1>
                    </Col>
                    <Col lg={14} xl={14} className="justify-in-end">
                        <div className="search-bar-width mr-20">
                            <Form>
                                <Input.Group compact size="large" className="d-inline">
                                    <Input
                                        placeholder="Search..."
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        className="manrope f-14 mat-search-bar"
                                    />
                                </Input.Group>
                            </Form>
                        </div>
                    </Col>
                </Row>
                
                <InfiniteScrollGallery
                    type="ai_explore"
                    images={filteredImages}
                    loading={exploreLoading}
                    useAntdPreview={true}
                    onVideoClick={handleVideoClick}
                    onImageClick={handleImageClick}
                    searchValue={searchValue}
                />
            </div>
            <Modal
                visible={videoModalVisible}
                onCancel={() => {
                    setVideoModalVisible(false);
                    setCurrentVideoUrl("");
                }}
                footer={null}
                width="min(90vw, 960px)"
                centered
                destroyOnClose
                bodyStyle={{ padding: 0 }}
            >
                {currentVideoUrl && (
                    <div style={{ position: 'relative', paddingTop: '56.25%', background: 'black' }}>
                        <ReactPlayer
                            url={currentVideoUrl}
                            width="100%"
                            height="100%"
                            playing={true}
                            controls={true}
                            style={{ position: 'absolute', top: 0, left: 0 }}
                        />
                    </div>
                )}
            </Modal>
        </CustomerMainLayout>
    );
};

export default Explore;
